import { useEffect, useState } from 'react';
import { Fonts, Spacings } from '@styles/index';
import styled from '@emotion/styled';
import Accordion from 'aquilla/core/molecules/Accordion';
import Typography from 'aquilla/core/atoms/Typography';
import RadioButton from '@cont/StoreListing/RadioButton';
import MultiCheckBox from '@cont/StoreListing/MultiCheckBox';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { ALL, SINGLE_SELECT, STORE_TAB_FILTER_CLICKED } from './constants';

const SubHeading = styled(Typography)`
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_6B};
`;
const FiltersApplied = styled.div`
  display: flex;
  align-items: center;
  background: var(--color-i-divider);
  color: var(--color-text-secondary);
  height: ${Spacings.SPACING_5B};
  width: ${Spacings.SPACING_5B};
  text-align: center;
  border-radius: ${Spacings.SPACING_10};
  justify-content: center;
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.REGULAR};
`;

const StyledAccordion = styled(Accordion)`
  border: none;
  padding: 0;
  background-color: var(--color-i-base-0);

  & .accordion-description.expanded {
    padding-top: ${Spacings.SPACING_0B};
    color: var(--color-text-secondary);
    margin-bottom: ${Spacings.SPACING_26};
  }
  &::-webkit-scrollbar {
    display: none;
  }
  &:hover {
    background: none;
  }

  & .accordion-header-action-icon {
    margin-right: ${Spacings.SPACING_4B};
  }

  & .accordion-header-title {
    justify-content: space-between;
  }

  & .aquilla-accordion-header-label {
    width: 64px;
    margin-right: ${Spacings.SPACING_2B};
  }
`;

const StraightLine = styled.div`
  border: 0.5px solid var(--color-i-divider);
`;

const ClearAll = styled.div`
  text-align: center;
  transition: 0.3s ease;
  text-decoration: underline;
  padding: 2px 0px;
  margin-left: ${Spacings.SPACING_4B};
  cursor: pointer;
  color: var(--color-text-primary);
  z-index: 200;
  &:hover {
    transform: translateY(-1px);
  }
  & .aquilla-typography {
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const Label = styled.div`
  position: absolute;
  top: 24px;
  right: 48px;
`;

const AccordionWrapper = styled.div`
  position: relative;
`;

const Filter = ({
  filter,
  onChange,
  index,
  filterObj,
  resetFilters,
  onClear,
  getAllOptionsByFilterName
}) => {
  const [isExpanded, setIsExpanded] = useState(index < 2);
  const selectedFilters = filterObj[filter?.key]?.length ?? 0;
  const allOptions = getAllOptionsByFilterName(filter.key);

  const goalUID = useRouter().query.goalUID as string;
  const goalSegmentData = useGoalEventProps({ goalUID });

  const handleIsExpanded = (expanded) => {
    setIsExpanded(expanded);
    if (expanded) {
      segment.track(STORE_TAB_FILTER_CLICKED, {
        ...goalSegmentData,
        filter_type: filter.label
      });
    }
  };

  const hasAllOption = allOptions.find((option) => {
    return option === ALL;
  });

  const handleClearAll = (e) => {
    onClear(filter);
  };

  const getLabel = () => {
    if (isExpanded && !!hasAllOption && selectedFilters > 1) {
      return (
        <ClearAll id="clear-all" onClick={handleClearAll}>
          <Typography variant="p3">Clear all</Typography>
        </ClearAll>
      );
    }

    if (!isExpanded && selectedFilters) {
      return <FiltersApplied>{selectedFilters}</FiltersApplied>;
    }
  };

  return filter.key === 'sort_by' ? null : (
    <>
      {filter.key === SINGLE_SELECT ? (
        <div key={String(index)}>
          <SubHeading variant="h6">{filter.label.toUpperCase()}</SubHeading>
          <RadioButton
            options={filter.values}
            filterName={filter.key}
            filterType={filter.selectType}
            selected={filterObj[filter.key]}
            onChange={onChange}
          />
          <StraightLine />
        </div>
      ) : (
        <AccordionWrapper key={String(index)}>
          <StyledAccordion
            key={String(index)}
            title={
              <SubHeading variant="h6">{filter.label.toUpperCase()}</SubHeading>
            }
            onToggleExpand={(expanded) => {
              handleIsExpanded(expanded);
            }}
            expanded={index < 2 || isExpanded}
          >
            <MultiCheckBox
              options={filter.values}
              filterType={filter.selectType}
              selected={filterObj[filter.key]}
              filterName={filter.key}
              onChange={onChange}
            />
          </StyledAccordion>
          <Label>{getLabel()}</Label>
          <StraightLine />
        </AccordionWrapper>
      )}
    </>
  );
};

export default Filter;
